import React, { Fragment, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import Router from "@components/router.js";
import Popover from "../Popover/Popover";
import Modal from "../Modal/Modal";
import profile from "@images/profile.svg";
import info from "@images/info-gray.svg";
import edit from "@images/edit.svg";
import block_link from "@images/block_link.svg";

function importAll(r) {
  let images = {};
  r.keys().forEach((item) => {
    const iconName = item.replace("./", "").split(".")[0];
    images[iconName] = r(item);
  });
  return images;
}

const iconImages = importAll(
  require.context("../../images/", false, /\.(png|jpe?g|svg)$/)
);

const Show = ({ data }) => {
  const { main_info, icons: initialIcons, redirect_on: initialRedirect } = data;
  const [redirectOn, setRedirectOn] = useState(initialRedirect);
  const [icons, setIcons] = useState(initialIcons);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const standart = main_info.user_mode === "standard";

  const truncateText = (text, maxLength) => {
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  };

  const onDragEnd = (result) => {
    const { destination, source } = result;
    if (!destination) return;
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const updated = Array.from(icons);
    const [moved] = updated.splice(source.index, 1);
    updated.splice(destination.index, 0, moved);

    setIcons(updated);

    const contacts = updated.map((icon) => icon.name);

    Router.fetchPatchIcons("/profile/sort", JSON.stringify({ contacts }));
  };

  const handleRedirect = async () => {
    const newRedirectValue = !redirectOn;

    try {
      await Router.fetchRedirect(
        "/profile.json",
        JSON.stringify({ profile: { is_redirect: newRedirectValue } })
      );
      setRedirectOn(newRedirectValue);
    } catch (error) {
      console.error("Failed to update redirect setting:", error);
    }
  };

  const handleIconClick = (shouldBlur, href) => (e) => {
    if (shouldBlur) {
      e.preventDefault();
      setIsModalOpen(true);
    }
  };

  return (
    <div className="profile-container">
      <div className="avatar-container">
        <a href="/profile/edit">
          {main_info.avatar ? (
            <img
              key={main_info.avatar}
              src={`${main_info.avatar}?t=${new Date().getTime()}`}
              alt={main_info.full_name}
              className="avatar"
            />
          ) : (
            <img
              src={profile}
              alt={main_info.full_name}
              className="avatar-absent"
            />
          )}
        </a>
      </div>

      <div className="info-container">
        <h1 className="full-name">{main_info.full_name}</h1>
        {main_info.birthday && <p className="birthday">{main_info.birthday}</p>}
        {main_info.position && <p className="position">{main_info.position}</p>}
        {main_info.company && <p className="company">{main_info.company}</p>}
        {main_info.bio && (
          <p className={`bio ${standart && "blurred"}`}>
            {main_info.bio.split(/\r\n|\r|\n/).map((line, i) => (
              <Fragment key={i}>
                {line}
                <br />
              </Fragment>
            ))}
          </p>
        )}
      </div>

      <div className="redirect-container">
        <button
          className={redirectOn ? "redirect-button-on" : "redirect-button"}
          onClick={handleRedirect}
        >
          {I18n.t("redirect")} {redirectOn ? "ON" : "OFF"}
        </button>
        <Popover content={I18n.t("redirect_popup")} redirect />
        <img
          src={edit}
          alt="edit"
          onClick={() => {
            window.location.href = "/profile/edit";
          }}
        />
      </div>

      <div className="redirect-hint">{I18n.t("redirect_hint")}</div>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="icons-droppable">
          {(provided) => (
            <div
              className="icons-grid"
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {icons.map((icon, index) => {
                const shouldBlur = standart && index > 1;

                return (
                  <Draggable
                    key={icon.name}
                    draggableId={icon.name}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <a
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        href={icon.value}
                        onClick={(e) => {
                          if (shouldBlur && !snapshot.isDragging) {
                            e.preventDefault();
                            setIsModalOpen(true);
                          } else {
                            handleIconClick(false, icon.value);
                          }
                        }}
                        target={icon.target || "_self"}
                        className={`icon-item ${shouldBlur ? "blurred" : ""} ${
                          redirectOn && index > 0 ? "grayscale" : ""
                        } ${snapshot.isDragging ? "dragging" : ""}`}
                      >
                        <div className="icon">
                          <img
                            src={iconImages[icon.name]}
                            alt={icon.title}
                            className={`icon-image ${
                              redirectOn && index > 0 ? "grayscale" : ""
                            }`}
                            onError={(e) => {
                              e.target.src = iconImages["default"];
                            }}
                          />
                        </div>
                        <span
                          className={`icon-title ${
                            redirectOn && index > 0 ? "grayscale" : ""
                          }`}
                        >
                          {truncateText(icon.title, 24)}
                        </span>
                      </a>
                    )}
                  </Draggable>
                );
              })}

              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {standart && (
        <div className="tariff-hint">
          <div className="tariff-hint-image">
            <img src={info} alt="info" width="20" height="22" />
            <div className="tariff-hint-text">
              {I18n.t("profiles.show.tariff_hint")}
            </div>
          </div>

          <div className="tariff-hint-buttons">
            {/* TODO: добавить когда будет готова реф. программа */}

            {/* <button className="tariff-hint-button-free">
              {I18n.t("profiles.show.go_to_free")}
            </button> */}

            <button
              className="tariff-hint-button-tariffs"
              onClick={() => {
                window.location.href = "/plan";
              }}
            >
              {I18n.t("profiles.show.tariffs")}
            </button>
          </div>
        </div>
      )}

      <a
        href={
          main_info.slug
            ? `https://yourcf.ru/?utm_source=yourcf&utm_medium=online&utm_campaign=${main_info.slug}`
            : "https://yourcf.ru/?utm_source=yourcf&utm_medium=online&utm_campaign=footer"
        }
        className="slug-button"
        target="_blank"
      >
        {I18n.t("copyright")}
      </a>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <div className="show-modal-content">
          <img src={block_link} alt="block_link" />

          <div className="show-modal-content-title">
            {I18n.t("profiles.show.modal.title")}
          </div>

          <div className="show-modal-content-text">
            {I18n.t("profiles.show.modal.description")}
          </div>

          <button
            className="tariff-hint-button-tariffs"
            onClick={() => {
              window.location.href = "/plan";
            }}
          >
            {I18n.t("profiles.show.modal.more_button")}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default Show;
